import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  Textarea,
  UnorderedList,
  ListItem,
} from '@chakra-ui/react';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { INVOICE_TAX_OVERRIDE_MIN_REASON } from 'utils/invoiceTaxApi';

/**
 * RAT-1913 AC3/AC4: the tax registry has no record of this client's tax number.
 *
 * The dialog states the consequence rather than asking a yes/no question, and
 * takes a reason, because confirming here puts a declaration that the client is
 * not registered (no censado) in front of the tax authority under the
 * operator's name.
 *
 * `canOverride` false means there is nothing to confirm - a malformed number
 * cannot be transmitted at all, and a self-billed invoice makes the merchant
 * the taxpayer, which cannot be declared unregistered. Then this is a dead end
 * with one way out: fix the tax number.
 */
const InvoiceTaxOverrideModal = ({
  isOpen,
  onClose,
  onConfirm,
  loader,
  merchantName,
  taxNumber,
  message,
  registryLegalName,
  canOverride,
}) => {
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (isOpen) {
      setReason('');
    }
  }, [isOpen]);

  const reasonIsUsable =
    reason.trim().length >= INVOICE_TAX_OVERRIDE_MIN_REASON;

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="18px">
          Tax number not found in the AEAT census
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Alert status="warning" borderRadius="md" alignItems="flex-start">
            <AlertIcon />
            <Box>
              <Text fontWeight="medium">
                {merchantName}
                {taxNumber ? ` (${taxNumber})` : ''}
              </Text>
              <Text fontSize="14px" mt={1}>
                {message}
              </Text>
            </Box>
          </Alert>

          {registryLegalName ? (
            <Text fontSize="14px" mt={4}>
              The registry holds the legal name{' '}
              {/* Company names routinely end in "S.L." - the sentence supplies
                  its own full stop, so one is trimmed off the name to avoid
                  rendering "EIVICOCOS S.L..". */}
              <strong>{registryLegalName.replace(/\.\s*$/, '')}</strong>.
            </Text>
          ) : null}

          {canOverride ? (
            <>
              <Text fontSize="14px" mt={4}>
                If you are sure the tax number is right, you can issue the
                invoice anyway. What happens then:
              </Text>
              <UnorderedList fontSize="14px" mt={2} spacing={1}>
                <ListItem>
                  The invoice declares this client as not registered (no
                  censado).
                </ListItem>
                <ListItem>
                  AEAT will accept it with errors, and it may need a corrective
                  invoice later.
                </ListItem>
                <ListItem>
                  Your name, the time and your reason are stored with the
                  invoice.
                </ListItem>
              </UnorderedList>

              <FormControl mt={4} isRequired>
                <FormLabel fontSize="14px">
                  Why are you sure this tax number is correct?
                </FormLabel>
                <Textarea
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder="e.g. Confirmed with the client on 20 Aug, AEAT census not yet updated"
                  rows={3}
                />
              </FormControl>
            </>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" mr={3} onClick={onClose}>
            {canOverride ? 'Go back and fix' : 'Close'}
          </Button>
          {canOverride ? (
            <Button
              colorScheme="red"
              onClick={() => onConfirm(reason.trim())}
              isDisabled={loader || !reasonIsUsable}
            >
              {loader ? <Spinner size="sm" /> : 'Confirm and issue'}
            </Button>
          ) : null}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

InvoiceTaxOverrideModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  loader: PropTypes.bool,
  merchantName: PropTypes.string,
  taxNumber: PropTypes.string,
  message: PropTypes.string,
  registryLegalName: PropTypes.string,
  canOverride: PropTypes.bool,
};

export default InvoiceTaxOverrideModal;
